import * as XLSX from 'xlsx'

import {
  appendStyledSheet,
  calculatePercent,
  capitalizeFirst,
  createReportHeader,
  formatMetric,
} from '@/shared/lib/dashboardExcelFormat'
import {
  formatProductNames,
  formatProductQuantities,
} from '@/shared/lib/exportFormat'

/**
 * Build and download a professional, readable Excel file for the Sales Dashboard.
 */
export function exportSalesDashboardToExcel({
  campaignLeads,
  conversionRate,
  ordersClosed,
  ordersLost,
  leadsManagement,
  leadsNotFollowedUp = [],
  target = 0,
  achievement = 0,
  period = 'all',
  customRange = {},
} = {}) {
  const workbook = XLSX.utils.book_new()
  const exportedAt = new Date().toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })

  // ---------------------------------------------------------------------------
  // 1. EXECUTIVE SUMMARY SHEET (Consistent 4-column layout for perfect readability)
  // ---------------------------------------------------------------------------
  const myTarget = Number(target) || Number(ordersClosed?.target) || 0
  const wonOrders =
    Number(conversionRate?.wonCount) ||
    Number(conversionRate?.conversions) ||
    Number(ordersClosed?.total) ||
    0
  const myAchieved =
    Number(achievement) || Number(ordersClosed?.achievement) || wonOrders
  const achievementRate = calculatePercent(myAchieved, myTarget)

  const lostOrders =
    Number(conversionRate?.lostCount) || Number(ordersLost?.total) || 0
  const totalAssignedLeads =
    Number(conversionRate?.total) || Number(campaignLeads?.total) || 0
  const totalClosedDeals = wonOrders + lostOrders
  const winRate = conversionRate?.percentage
    ? `${conversionRate.percentage}%`
    : calculatePercent(wonOrders, totalClosedDeals || totalAssignedLeads)

  const summaryRows = [
    ...createReportHeader({
      title: 'Sales Performance Dashboard',
      subtitle:
        'Individual Sales Targets, Orders Won/Lost, and Pipeline Health',
      period,
      customRange,
      exportedAt,
    }),
    ['KEY PERFORMANCE INDICATORS (KPIs)'],
    [
      'Category',
      'Metric Name',
      'Performance Value',
      'Context & Benchmark Notes',
    ],
    [
      'Sales Targets',
      'My Sales Target Quota',
      formatMetric(myTarget, 'Units'),
      'Assigned sales target quota for the period',
    ],
    [
      'Sales Targets',
      'My Actual Achievement',
      formatMetric(myAchieved, 'Units'),
      'Confirmed won order units in period',
    ],
    [
      'Sales Targets',
      'Target Achievement Rate',
      achievementRate,
      'Achieved vs assigned target quota',
    ],
    [
      'Pipeline',
      'Total Assigned Leads',
      formatMetric(totalAssignedLeads, 'Leads'),
      capitalizeFirst(campaignLeads?.subtitle) ||
        'Total active leads assigned to me',
    ],
    [
      'Conversion Funnel',
      'Orders Won (Conversions)',
      formatMetric(wonOrders, 'Orders'),
      capitalizeFirst(ordersClosed?.subtitle) ||
        'Deals successfully converted and closed as Won',
    ],
    [
      'Conversion Funnel',
      'Orders Lost',
      formatMetric(lostOrders, 'Orders'),
      capitalizeFirst(ordersLost?.subtitle) ||
        'Deals marked as Lost or dropped',
    ],
    [
      'Conversion Funnel',
      'Total Closed Deals',
      formatMetric(totalClosedDeals, 'Deals'),
      'Sum of won orders and lost orders',
    ],
    [
      'Conversion Funnel',
      'Win / Conversion Rate',
      winRate,
      capitalizeFirst(conversionRate?.highlight) ||
        'Won orders share of closed deals',
    ],
    [
      'Action Items',
      'Leads Pending Follow-up',
      formatMetric(
        Array.isArray(leadsNotFollowedUp) ? leadsNotFollowedUp.length : 0,
        'Leads',
      ),
      'Active leads requiring timely follow-up action',
    ],
    [],
    [],
  ]

  // Table 2: Leads by Source
  const sourceItems = campaignLeads?.sources ?? []
  const totalSources = sourceItems.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0,
  )

  summaryRows.push(
    ['ASSIGNED LEADS BY SOURCE CHANNEL'],
    [
      'Metric Group',
      'Lead Source Channel',
      'Leads Count',
      'Share of Total (%)',
    ],
  )
  if (sourceItems.length === 0) {
    summaryRows.push([
      'Lead Source',
      'No source channel data available',
      '0 Leads',
      '0.0%',
    ])
  } else {
    sourceItems.forEach((source) => {
      const count = Number(source.count) || 0
      summaryRows.push([
        'Lead Source',
        capitalizeFirst(source.name),
        formatMetric(count, 'Leads'),
        `${calculatePercent(count, totalSources || totalAssignedLeads)} of assigned leads`,
      ])
    })
    summaryRows.push([
      'Summary Total',
      'TOTAL ASSIGNED LEADS',
      formatMetric(totalSources || totalAssignedLeads, 'Leads'),
      '100.0%',
    ])
  }

  summaryRows.push([], [])

  // Table 3: Pipeline Status Breakdown
  const statusItems = leadsManagement?.status?.items ?? []
  const totalStatus = statusItems.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0,
  )

  summaryRows.push(
    ['PIPELINE STATUS BREAKDOWN'],
    [
      'Metric Group',
      'Pipeline Stage / Status',
      'Deals Count',
      'Share of Total (%)',
    ],
  )
  if (statusItems.length === 0) {
    summaryRows.push([
      'Pipeline Status',
      'No status data available',
      '0 Deals',
      '0.0%',
    ])
  } else {
    statusItems.forEach((item) => {
      const count = Number(item.count) || 0
      summaryRows.push([
        'Pipeline Status',
        capitalizeFirst(item.name),
        formatMetric(count, 'Deals'),
        `${calculatePercent(count, totalStatus)} of pipeline`,
      ])
    })
    summaryRows.push([
      'Summary Total',
      'TOTAL IN PIPELINE',
      formatMetric(totalStatus, 'Deals'),
      '100.0%',
    ])
  }

  summaryRows.push([], [])

  // Table 4: Lead Qualification Breakdown
  const qualItems = leadsManagement?.qualification?.items ?? []
  const totalQual = qualItems.reduce(
    (sum, s) => sum + (Number(s.count) || 0),
    0,
  )

  summaryRows.push(
    ['LEAD QUALIFICATION (WARMTH BREAKDOWN)'],
    ['Metric Group', 'Warmth Level', 'Leads Count', 'Share of Total (%)'],
  )
  if (qualItems.length === 0) {
    summaryRows.push([
      'Qualification',
      'No qualification data available',
      '0 Leads',
      '0.0%',
    ])
  } else {
    qualItems.forEach((item) => {
      const count = Number(item.count) || 0
      summaryRows.push([
        'Qualification',
        capitalizeFirst(item.name),
        formatMetric(count, 'Leads'),
        `${calculatePercent(count, totalQual)} of qualified leads`,
      ])
    })
    summaryRows.push([
      'Summary Total',
      'TOTAL QUALIFIED LEADS',
      formatMetric(totalQual, 'Leads'),
      '100.0%',
    ])
  }

  appendStyledSheet(workbook, 'Executive Summary', summaryRows)

  // ---------------------------------------------------------------------------
  // 2. PRODUCTS WON & LOST SHEET
  // ---------------------------------------------------------------------------
  const wonProducts = ordersClosed?.products ?? []
  const lostProducts = ordersLost?.products ?? []

  const totalWonUnits = wonProducts.reduce(
    (sum, p) => sum + (Number(p.units ?? p.count) || 0),
    0,
  )
  const totalLostUnits = lostProducts.reduce(
    (sum, p) => sum + (Number(p.units ?? p.count) || 0),
    0,
  )

  const productRows = [
    ...createReportHeader({
      title: 'Products Performance (Won vs Lost)',
      subtitle: 'Products sold in closed won orders vs closed lost orders',
      period,
      customRange,
      exportedAt,
    }),
    ['ORDERS WON BY PRODUCT'],
    ['Order Status', 'Product Name', 'Units Sold', 'Share of Won Units (%)'],
  ]

  if (wonProducts.length === 0) {
    productRows.push(['Won Orders', 'No products sold yet', '0 Units', '0.0%'])
  } else {
    wonProducts.forEach((product) => {
      const units = Number(product.units ?? product.count) || 0
      productRows.push([
        'Won Orders',
        product.name,
        formatMetric(units, 'Units'),
        calculatePercent(units, totalWonUnits),
      ])
    })
    productRows.push([
      'Summary Total',
      'TOTAL WON UNITS',
      formatMetric(totalWonUnits, 'Units'),
      '100.0%',
    ])
  }

  productRows.push(
    [],
    [],
    ['ORDERS LOST BY PRODUCT'],
    ['Order Status', 'Product Name', 'Units Lost', 'Share of Lost Units (%)'],
  )

  if (lostProducts.length === 0) {
    productRows.push([
      'Lost Orders',
      'No lost products recorded',
      '0 Units',
      '0.0%',
    ])
  } else {
    lostProducts.forEach((product) => {
      const units = Number(product.units ?? product.count) || 0
      productRows.push([
        'Lost Orders',
        product.name,
        formatMetric(units, 'Units'),
        calculatePercent(units, totalLostUnits),
      ])
    })
    productRows.push([
      'Summary Total',
      'TOTAL LOST UNITS',
      formatMetric(totalLostUnits, 'Units'),
      '100.0%',
    ])
  }

  appendStyledSheet(workbook, 'Products Won & Lost', productRows)

  // ---------------------------------------------------------------------------
  // 3. PENDING FOLLOW-UPS SHEET
  // ---------------------------------------------------------------------------
  const followUpRows = [
    ...createReportHeader({
      title: 'My Leads Pending Follow-up',
      subtitle: 'Active leads requiring contact, updates, or follow-up action',
      period,
      customRange,
      exportedAt,
    }),
    ['ACTION QUEUE: LEADS PENDING FOLLOW-UP'],
    [
      '#',
      'Lead Name',
      'Company Name',
      'Channel Source',
      'Pending Duration',
      'Interested Product(s)',
      'Estimated Quantity',
      'Email Address',
      'Phone Number',
    ],
  ]

  if (!Array.isArray(leadsNotFollowedUp) || leadsNotFollowedUp.length === 0) {
    followUpRows.push([
      '—',
      'All assigned leads are followed up — great job!',
      '—',
      '—',
      '—',
      '—',
      '—',
      '—',
      '—',
    ])
  } else {
    leadsNotFollowedUp.forEach((lead, index) => {
      const productNames =
        formatProductNames(lead.products) || lead.product || '—'
      const quantities = formatProductQuantities(lead.products) || '—'

      followUpRows.push([
        index + 1,
        lead.name || '—',
        lead.company || '—',
        lead.source || lead.platform || '—',
        lead.pending || 'Action Pending',
        productNames,
        quantities,
        lead.email || '—',
        lead.phone || '—',
      ])
    })
  }

  appendStyledSheet(workbook, 'Pending Follow-ups', followUpRows)

  // ---------------------------------------------------------------------------
  // Write Workbook
  // ---------------------------------------------------------------------------
  const stamp = new Date().toISOString().slice(0, 10)
  XLSX.writeFile(workbook, `sales-dashboard-${stamp}.xlsx`)
}
